import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Avatar, Box, Button, CircularProgress, Typography,
} from '@material-ui/core';
import { api } from 'src/service';

// Backoffice profile-photo manager for a client. Renders the avatar,
// supports upload (via /image_resource) and removal (PATCH .../photo with
// {profile_resource_id: null}).
const ClientProfilePhoto = ({ clientId, initialResourceId }) => {
  const [resourceId, setResourceId] = useState(initialResourceId || null);
  const [url, setUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    setResourceId(initialResourceId || null);
  }, [initialResourceId]);

  useEffect(() => {
    let cancelled = false;
    if (!resourceId) { setUrl(null); return undefined; }
    api
      .get(`/image_resource/${resourceId}`)
      .then((res) => {
        if (cancelled) return;
        const u = res && res.data && res.data.data && (res.data.data.url || res.data.data.url_image);
        setUrl(u || null);
      })
      .catch(() => { if (!cancelled) setUrl(null); });
    return () => { cancelled = true; };
  }, [resourceId]);

  const readFileAsBase64 = (file) => new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onerror = () => reject(r.error);
    r.onload = () => {
      const s = r.result || '';
      const i = s.indexOf(',');
      resolve(i >= 0 ? s.slice(i + 1) : s);
    };
    r.readAsDataURL(file);
  });

  const onPickFile = async (e) => {
    setError(null);
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (!/^image\//.test(file.type)) {
      setError('Pick an image file (jpeg/png/webp).');
      return;
    }
    setBusy(true);
    try {
      const base64 = await readFileAsBase64(file);
      const upload = await api.post('/image_resource', {
        name: file.name,
        mime_type: file.type,
        type: 'profile',
        size: file.size,
        base64,
      });
      const newId = (upload && upload.data && upload.data.data && upload.data.data.resource_id);
      if (!newId) throw new Error('upload did not return a resource_id');
      await api.patch(`/backoffice/client/${clientId}/photo`, { profile_resource_id: newId });
      setResourceId(newId);
    } catch (err) {
      setError(err.message || 'upload failed');
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const onRemove = async () => {
    if (!resourceId) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm('Remove this client\u2019s profile photo?')) return;
    setBusy(true);
    setError(null);
    try {
      await api.patch(`/backoffice/client/${clientId}/photo`, { profile_resource_id: null });
      setResourceId(null);
    } catch (err) {
      setError(err.message || 'remove failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box display="flex" alignItems="center" gridGap={16} flexWrap="wrap" pb={2}>
      <Avatar src={url || undefined} style={{ width: 96, height: 96 }} />
      <Box>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={onPickFile}
        />
        <Box display="flex" gridGap={8}>
          <Button
            variant="outlined"
            disabled={busy}
            onClick={() => fileRef.current && fileRef.current.click()}
          >
            {resourceId ? 'Replace photo' : 'Upload photo'}
          </Button>
          {resourceId && (
            <Button color="secondary" disabled={busy} onClick={onRemove}>
              Remove
            </Button>
          )}
          {busy && <CircularProgress size={20} />}
        </Box>
        {error && (
          <Typography color="error" variant="caption">{error}</Typography>
        )}
        {!error && resourceId && (
          <Typography variant="caption" color="textSecondary">
            resource_id: {resourceId}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

ClientProfilePhoto.propTypes = {
  clientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  initialResourceId: PropTypes.string,
};

export default ClientProfilePhoto;
